/// <amd-module name="Repos/Changes/Util" />
import { IGitChangeItem, IGitCommitChange, ILocalFileChange, ILocalFileChangesCollection, ILocalFileChangesEvent } from "Repos/Changes/Contracts";
import { IChange, IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext, GitVersionDescriptor, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { getIconNameForFile } from "Repos/Common/Util/File";
import { IGitItemContent } from "Repos/Files/Contracts";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { IReadonlyObservableArray, IReadonlyObservableValue, Observable, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext } from "VSS/Platform/Context";
import { IIconProps } from "VSSUI/Icon";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";

/**
 * Gets an IChangeCollection for the given set of IChange's
 *
 * @param changes Changes to process
 * @param repositoryName Name of the repository (for the root tree item)
 * @param sourceCommitId Commit id of the source branch of the changes being merged in
 * @param targetVersion Target version to compare each change to
 * @param loadMoreChanges Callback to use to load more changes if not all changes are included in this collection
 */
export function getChangesCollection<TVersionDescriptor extends { version: string }>(
    changes: IGitCommitChange[],
    repositoryName: string,
    sourceCommitId: string,
    sourceVersion: TVersionDescriptor,
    targetVersion: TVersionDescriptor,
    loadMoreChanges?: () => void,
    key?: string,
    checkmarksMap: { [path: string]: boolean } = {},
    pathPrefix: string = ""
): IChangeCollection<TVersionDescriptor> {
    // Add the repo tree item.  Root of entire tree.
    const repoItem: IChange = {
        checkmarked: new ObservableValue<boolean>(checkmarksMap["/"]),
        iconProps: { iconName: "GitLogo" },
        isFolder: true,
        isInvalid: false,
        name: repositoryName,
        folder: "/",
        path: "/",
        commitId: sourceCommitId,
        changeType: VersionControlChangeType.None,
        objectId: undefined
    };

    // Initialize the collection
    const collection: IChangeCollection<TVersionDescriptor> = {
        changesByPath: {},
        key: key || `${sourceCommitId}.${targetVersion.version}`,
        loadMoreChanges,
        rootTreeItem: {
            expanded: true,
            childItems: [],
            data: repoItem
        },
        sourceVersion,
        targetVersion,
        totalChanges: 0,
        treeItemsByPath: {}
    };

    collection.changesByPath["/"] = repoItem;
    collection.treeItemsByPath["/"] = collection.rootTreeItem;

    // Keep track of children per folder
    const folderChildItems: { [folder: string]: ITreeItem<IChange>[] } = {};

    // Keep track of all folders added in tree
    const treeFolders: string[] = [];

    treeFolders.push("/");
    folderChildItems["/"] = [];

    if (changes) {
        // Loop through each change and add to tree
        for (const change of changes) {
            // Skip rename/delete changes and folder items
            if (
                ((change.changeType & VersionControlChangeType.Delete) === VersionControlChangeType.Delete &&
                    (change.changeType & VersionControlChangeType.SourceRename) === VersionControlChangeType.SourceRename) ||
                (change.item.isFolder && !change.item.isEmptyFolder)
            ) {
                continue;
            }

            collection.totalChanges++;

            // Split the path into folders/leaf item
            const itemPath = change.item.path || "";
            const changeParts: string[] = itemPath.split("/");
            if (changeParts.length > 1) {
                let path = pathPrefix + "/";
                let parent = collection.rootTreeItem;
                for (let i = 1; i < changeParts.length; i++) {
                    if (i === changeParts.length - 1) {
                        // This is a leaf item.  Create the tree item and add to root items to display
                        const isFolder = change.item.isEmptyFolder || false;
                        const commitLeafItem: IChange = {
                            checkmarked: new ObservableValue<boolean>(checkmarksMap[itemPath]),
                            isEmptyFolder: change.item.isEmptyFolder,
                            isFolder,
                            isInvalid: false,
                            path: itemPath,
                            folder: parent.data.path,
                            name: changeParts[i],
                            changeType: change.changeType,
                            commitId: sourceCommitId,
                            objectId: change.objectId,
                            iconProps: getIconProps(change.item, itemPath),
                            sourcePath: change.item.originalPath || itemPath,
                            trackingId: change.item.trackingId
                        };

                        collection.changesByPath[itemPath] = commitLeafItem;

                        const commitLeafTreeItem = { data: commitLeafItem };
                        folderChildItems[parent.data.path].push(commitLeafTreeItem);

                        if (isFolder) {
                            treeFolders.push(itemPath);
                            folderChildItems[itemPath] = [];
                            collection.treeItemsByPath[itemPath] = commitLeafTreeItem;
                        }
                    } else {
                        if (i === 1) {
                            path = path + changeParts[i];
                        } else {
                            path = path + "/" + changeParts[i];
                        }

                        // See if folder exists.  If not create one.
                        let commitTreeItem: ITreeItem<IChange> | undefined = collection.treeItemsByPath[path];
                        if (!commitTreeItem) {
                            const folderItem = {
                                changeType: VersionControlChangeType.None,
                                checkmarked: new ObservableValue<boolean>(checkmarksMap[path]),
                                commitId: sourceCommitId,
                                iconProps: { iconName: "FabricFolderFill", className: "repos-folder-icon" },
                                isInvalid: false,
                                isFolder: true,
                                name: changeParts[i],
                                objectId: undefined,
                                path: path,
                                folder: path
                            };

                            collection.changesByPath[path] = folderItem;
                            commitTreeItem = {
                                expanded: true,
                                childItems: [],
                                data: folderItem
                            };

                            treeFolders.push(path);
                            folderChildItems[path] = [];
                            collection.treeItemsByPath[path] = commitTreeItem;

                            if (!parent.childItems) {
                                parent.childItems = [];
                            }

                            parent.childItems!.push(commitTreeItem);
                        }

                        parent = commitTreeItem;
                    }
                }
            }
        }

        for (const p of treeFolders) {
            collection.treeItemsByPath[p] &&
                collection.treeItemsByPath[p]!.childItems &&
                collection.treeItemsByPath[p]!.childItems!.push(...folderChildItems[p]);
        }
    }

    // Try and collapse folders with only one child folder into a single tree node.
    for (const treeItem of collection.rootTreeItem.childItems!) {
        combineNodes(treeItem);
    }

    for (const p of treeFolders) {
        collection.treeItemsByPath[p] &&
            collection.treeItemsByPath[p]!.childItems &&
            collection.treeItemsByPath[p]!.childItems!.sort(function(a: ITreeItem<IChange>, b: ITreeItem<IChange>) {
                if (a.data.isFolder !== b.data.isFolder) {
                    return a.data.isFolder ? -1 : 1;
                } else {
                    return localeIgnoreCaseComparer(a.data.path, b.data.path);
                }
            });
    }

    return collection;
}

/**
 * Searches the given path in the provided changes collection.
 */
export function findChange<TVersionDescriptor>(changes: IChangeCollection<TVersionDescriptor>, path: string): IChange | undefined {
    const fileChange = changes.changesByPath[path];
    const treeItems = changes.treeItemsByPath[path];
    return fileChange || (treeItems && treeItems.data) || undefined;
}

/**
 * Gets the child items (recursively) for the given tree item
 *
 * @param changes Collection of all changes
 * @param path Path of the parent folder
 */
export function getChangesForPath<TVersionDescriptor>(changes: IChangeCollection<TVersionDescriptor>, path: string): IChange[] {
    const childItems: IChange[] = [];

    const changeTreeItem = changes.treeItemsByPath[path];
    if (changeTreeItem) {
        addChildItems(changeTreeItem, childItems);
    }

    return childItems;
}

function addChildItems(changeTreeItem: ITreeItem<IChange>, childItems: IChange[]): void {
    if (changeTreeItem.data.isFolder) {
        if (changeTreeItem.childItems) {
            for (const childItem of changeTreeItem.childItems) {
                addChildItems(childItem, childItems);
            }
        }
    } else {
        childItems.push(changeTreeItem.data);
    }
}

// This function will attempt to combine nodes in tree if only one child exists and it is a folder node.
// For example if the path is /foo/bar/baz.cs, there will be 3 nodes.  Folder nodes for foo and bar,
// as well as a leaf node for baz.cs.  After this function, there will be 2 nodes.  One folder node
// for /foo/bar and the lead node for baz.cs.  If foo had 2 children instead of 1, then foo and bar will
// not be combined.
function combineNodes(node: ITreeItem<IChange>): void {
    if (node && node.data.isFolder && node.childItems) {
        if (node.childItems.length === 1 && node.childItems[0].data.isFolder && !node.childItems[0].data.isEmptyFolder) {
            node.data.name = node.data.name + "/" + node.childItems[0].data.name;
            node.data.path = node.childItems[0].data.path;
            node.childItems = node.childItems[0].childItems;

            combineNodes(node);
        } else {
            for (const childNode of node.childItems) {
                combineNodes(childNode);
            }
        }
    }
}

function getIconProps(change: IGitChangeItem, path: string): IIconProps {
    return change.isFolder
        ? { iconName: "FabricFolderFill", className: "repos-folder-icon" }
        : change.isSymLink
        ? { iconName: "PageArrowRight" }
        : { iconName: getIconNameForFile(path) };
}

/**
 * Gets the range of text to mark as selected for a given line in a diff
 * and set of comment threads
 *
 * @param threads Comment threads for this file
 * @param lineLength Length of the line
 * @param oLineNumber Original side line number
 * @param mLineNumber Modified side line number
 */
export function getLineCommentPosition(
    threadContexts: CommentThreadContext[],
    lineLength: number,
    oLineNumber: number | undefined,
    mLineNumber: number | undefined
): { start?: number; end?: number } {
    const result: { start?: number; end?: number } = {};

    for (const threadContext of threadContexts) {
        let commentStart: number | undefined;
        let commentEnd: number | undefined;

        if (threadContext) {
            if (mLineNumber !== undefined && threadContext.rightFileStart && threadContext.rightFileEnd) {
                if (mLineNumber > threadContext.rightFileStart.line && mLineNumber <= threadContext.rightFileEnd.line) {
                    commentStart = 0;
                } else if (mLineNumber === threadContext.rightFileStart.line) {
                    commentStart = threadContext.rightFileStart.offset - 1;
                }
                if (mLineNumber < threadContext.rightFileEnd.line && mLineNumber >= threadContext.rightFileStart.line) {
                    commentEnd = lineLength;
                } else if (mLineNumber === threadContext.rightFileEnd.line) {
                    commentEnd = threadContext.rightFileEnd.offset - 1;
                }
            } else if (oLineNumber !== undefined && threadContext.leftFileStart && threadContext.leftFileEnd) {
                if (oLineNumber > threadContext.leftFileStart.line && oLineNumber <= threadContext.leftFileEnd.line) {
                    commentStart = 0;
                } else if (oLineNumber === threadContext.leftFileStart.line) {
                    commentStart = threadContext.leftFileStart.offset - 1;
                }
                if (oLineNumber < threadContext.leftFileEnd.line && oLineNumber >= threadContext.leftFileStart.line) {
                    commentEnd = lineLength;
                } else if (oLineNumber === threadContext.leftFileEnd.line) {
                    commentEnd = threadContext.leftFileEnd.offset - 1;
                }
            }
        }

        if (commentStart !== undefined && (result.start === undefined || result.start > commentStart)) {
            result.start = commentStart;
        }
        if (commentEnd !== undefined && (result.end === undefined || result.end < commentEnd)) {
            result.end = commentEnd;
        }
    }

    return result;
}

/**
 * Class for tracking local file changes
 */
export class LocalFileChangesCollection extends Observable<ILocalFileChangesEvent, "updateVersion" | "revert">
    implements ILocalFileChangesCollection {
    private dirty = new ObservableValue(false);
    private dirtyFiles = new ObservableArray<LocalFileChange>();
    private files: { [path: string]: LocalFileChange } = {};
    private pageContext: IVssPageContext;
    private originalVersion: GitVersionDescriptor;

    public get version(): GitVersionDescriptor {
        return this.originalVersion;
    }

    constructor(pageContext: IVssPageContext, version: GitVersionDescriptor) {
        super();

        this.pageContext = pageContext;
        this.originalVersion = version;
    }

    /**
     * Gets the set of dirty files
     */
    public getDirtyFiles(): IReadonlyObservableArray<ILocalFileChange> {
        return this.dirtyFiles;
    }

    public getFile(path: string): ILocalFileChange {
        let file = this.files[path];
        if (!file) {
            file = new LocalFileChange(path, () => {
                const gitFilesService = this.pageContext.getService<IGitFilesService>("IGitFilesService");
                return gitFilesService.getItemContentPromise(path, this.version);
            });
            this.files[path] = file;

            file.isDirty().subscribe(this.updateDirty);
        }
        return file;
    }

    public isDirty(): IReadonlyObservableValue<boolean> {
        return this.dirty;
    }

    public updateVersion(version: GitVersionDescriptor): void {
        const previousDirtyPaths = new Set<string>();

        this.originalVersion = version;

        const gitFilesService = this.pageContext.getService<IGitFilesService>("IGitFilesService");
        while(this.dirtyFiles.length > 0) {
            const file = this.dirtyFiles.value[0]
            if (file.isDirty().value) {
                previousDirtyPaths.add(file.path);
            }
            file.reset(() => gitFilesService.getItemContentPromise(file.path, version));
        }

        this.notify({ previousDirtyPaths }, "updateVersion");
    }

    public revertAll(): void {
        const previousDirtyPaths = new Set<string>();

        for (const file of this.dirtyFiles.value) {
            if (file.isDirty().value) {
                previousDirtyPaths.add(file.path);
            }
            file.revert();
        }

        this.notify({ previousDirtyPaths }, "revert");
    }

    private updateDirty = (): void => {
        this.dirtyFiles.value = Object.keys(this.files)
            .map(path => this.files[path])
            .filter(f => f.isDirty().value);

        const isDirty = this.dirtyFiles.length > 0;
        if (this.dirty.value !== isDirty) {
            this.dirty.value = isDirty;
        }
    };
}

class LocalFileChange implements ILocalFileChange {
    private dirty = new ObservableValue(false);
    private getOriginalItemContentMethod: () => Promise<IGitItemContent>;
    private text = new ObservableValue<string | undefined>(undefined);
    private originalText = new ObservableValue<string | undefined>(undefined);
    private originalItemContentPromise: Promise<IGitItemContent> | undefined;

    public readonly path: string;

    constructor(path: string, getOriginalItemContentMethod: () => Promise<IGitItemContent>) {
        this.path = path;
        this.getOriginalItemContentMethod = getOriginalItemContentMethod;
    }

    public getText(): IReadonlyObservableValue<string | undefined> {
        this.getOriginalItemContent();
        return this.text;
    }

    public isDirty(): IReadonlyObservableValue<boolean> {
        return this.dirty;
    }

    public getOriginalText(): IReadonlyObservableValue<string | undefined> {
        this.getOriginalItemContent();
        return this.originalText;
    }

    public getOriginalItemContent(): Promise<IGitItemContent> {
        if (!this.originalItemContentPromise) {
            this.originalItemContentPromise = this.getOriginalItemContentMethod().then(c => {
                this.originalText.value = c.details.content || "";
                if (this.text.value === undefined) {
                    this.text.value = this.originalText.value;
                } else {
                    this.updateDirty(this.text.value !== this.originalText.value);
                }
                return c;
            });
        }
        return this.originalItemContentPromise;
    }

    public reset(getOriginalItemContentMethod: () => Promise<IGitItemContent>): void {
        this.getOriginalItemContentMethod = getOriginalItemContentMethod;
        this.originalText.value = undefined;
        this.text.value = undefined;
        this.originalItemContentPromise = undefined;
        this.updateDirty(false);
    }

    public revert(): Promise<void> {
        if (this.originalText.value !== undefined) {
            this.setNewContent(this.originalText.value);
            return Promise.resolve();
        } else {
            return this.getOriginalItemContent().then(c => {
                this.setNewContent(c.details.content);
            });
        }
    }

    public setNewContent(newContent: string): void {
        this.getOriginalItemContent();
        this.text.value = newContent;
        this.updateDirty(this.text.value !== this.originalText.value);
    }

    private updateDirty(dirty: boolean): void {
        if (this.dirty.value !== dirty) {
            this.dirty.value = dirty;
        }
    }
}

export const getSelectionFromQueryParams = (pageContext: IVssPageContext) => {
    const pageHistoryService = pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
    const state = pageHistoryService.getProjectedState();

    if (state.line && state.lineStartColumn) {
        // Construct a selection from the URL
        const selection = {
            startLineNumber: parseInt(state.line),
            endLineNumber: parseInt(state.lineEnd || state.line),
            startColumn: parseInt(state.lineStartColumn),
            endColumn: parseInt(state.lineEndColumn || state.lineStartColumn),
            type: parseInt(state.type),
            orientation: parseInt(state.orientation)
        };


        // Remove these values from the URL
        delete state.line;
        delete state.lineEnd;
        delete state.lineStartColumn;
        delete state.lineEndColumn;
        delete state.lineStyle;
        delete state.type;
        delete state.orientation;
        pageHistoryService.queueState(state, HistoryBehavior.replace);

        return selection;
    }

    return undefined;
}